import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import LocationSearch from '@/components/LocationSearch';
import { 
  Store, CheckCircle, ArrowRight, Phone, 
  ShoppingBag, Utensils, Coffee, Scissors, Palette
} from 'lucide-react';

const businessTypes = [
  { icon: Store, title: 'Retail Stores', description: 'Fashion, electronics, lifestyle stores' },
  { icon: Utensils, title: 'Restaurants & Cafes', description: 'Dining spaces that delight' },
  { icon: Coffee, title: 'Salons & Spas', description: 'Relaxing wellness environments' },
  { icon: Scissors, title: 'Boutiques', description: 'Unique retail experiences' },
];

const benefits = [
  'Attract more foot traffic',
  'Increase average transaction value',
  'Create memorable brand experiences',
  'Optimize customer flow and journey',
  'Stand out from competition',
  'Maximize retail space efficiency'
];

const BusinessInterior = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    contactPerson: '',
    phone: '',
    email: '',
    location: '',
    area: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.businessName || !formData.phone || !formData.location) {
      toast.error('Please fill in all required fields including location');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/interior-business', {
        business_name: formData.businessName,
        business_type: formData.businessType || null,
        contact_person: formData.contactPerson || null,
        phone: formData.phone,
        email: formData.email || null,
        location: formData.location,
        area_sqft: formData.area ? parseInt(formData.area) : null,
        message: formData.message || null
      });
      
      toast.success(`Request submitted! Your Request ID: ${response.data.request_id}`);
      
      const whatsappMsg = `Hi ZenStudios! I need business interior design. Request ID: ${response.data.request_id}. Business: ${formData.businessName}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="business-interior-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Store className="w-4 h-4" /> Business Interiors
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Design Spaces That Drive Business Growth
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                From retail stores to restaurants, we create commercial spaces that attract 
                customers, enhance brand perception, and maximize revenue potential.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20business%20interior%20design.', '_blank')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1604754742629-3e5728249d73?w=600" 
                alt="Modern Retail Store"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Businesses We Serve</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Specialized design solutions for every type of commercial venture
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessTypes.map((type, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <type.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{type.title}</h3>
                <p className="text-sm text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300" 
                alt="Retail Interior"
                className="rounded-xl"
              />
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300" 
                alt="Restaurant Interior"
                className="rounded-xl mt-8"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Design Matters for Your Business</h2>
              <p className="text-gray-600 mb-8">
                First impressions matter. A well-designed space can increase dwell time by 40% 
                and boost sales by up to 25%. Make every square foot count.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20" id="contact-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transform Your Business Space</h2>
            <p className="text-gray-600">
              Tell us about your business and we'll create a design that drives results
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.businessName}
                    onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                    placeholder="Your business name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Business Type</Label>
                  <Input
                    value={formData.businessType}
                    onChange={(e) => setFormData({...formData, businessType: e.target.value})}
                    placeholder="e.g., Restaurant, Retail, Salon"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="business@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Location *</Label>
                  <LocationSearch
                    value={formData.location}
                    onChange={(val) => setFormData({...formData, location: val})}
                    placeholder="Search city / area..."
                    types="city,town,locality"
                    required
                    className="rounded-xl mt-1"
                    data-testid="business-interior-location"
                  />
                </div>
                <div>
                  <Label>Approximate Area (sq.ft)</Label>
                  <Input
                    type="number"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    placeholder="e.g., 1000"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Project Requirements</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Describe your vision, budget range, timeline..."
                  rows={4}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? 'Submitting...' : 'Get Free Design Consultation'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessInterior;
